import React, { useEffect, useRef, useState } from "react";
import { Cancel } from "@mui/icons-material";

export type TeamMemberInviteDetails = {
  first_name: string;
  last_name: string;
  email: string;
};

export const DUPLICATE_TEAM_MEMBER_EMAIL_MESSAGE =
  "A team member with this email address is already on your team.";

export const INVITE_TEAM_MEMBER_SUBMIT_LABEL = "Add Team Member";
export const INVITE_SUCCESS_AUTO_CLOSE_MS = 500;
export const INVITE_SUCCESS_HEADING = "Invite Will Be Sent";

type InviteTeamMemberModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (member: TeamMemberInviteDetails) => void;
  isMobile?: boolean;
  existingMemberEmails?: string[];
};

type InviteTeamMemberFormProps = {
  onCancel: () => void;
  onInvite: (member: TeamMemberInviteDetails) => void;
  isMobile?: boolean;
  existingMemberEmails?: string[];
};

const InviteTeamMemberForm: React.FC<InviteTeamMemberFormProps> = ({
  onCancel,
  onInvite,
  isMobile = false,
  existingMemberEmails = [],
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const touchClass = isMobile ? " touch-manipulation" : "";

  const isValid = Boolean(firstName.trim() && lastName.trim() && email.trim());
  const existingEmailSet = new Set(
    existingMemberEmails.map((value) => value.trim().toLowerCase())
  );

  const handleInvite = () => {
    if (!isValid) return;

    const normalizedEmail = email.trim().toLowerCase();
    if (existingEmailSet.has(normalizedEmail)) {
      setEmailError(DUPLICATE_TEAM_MEMBER_EMAIL_MESSAGE);
      return;
    }

    setEmailError(null);
    onInvite({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
    });
  };

  return (
    <div className={`flex flex-col w-full h-full text-left mx-auto${isMobile ? "" : " sm:w-11/12"}`}>
      <p className="font-raleway text-[16px] font-semibold text-black">Invite team member</p>
      <p className="font-open-sans text-[14px] text-[#808080] mt-1">
        Fill in the details below to invite a team member
      </p>

      <div className="flex flex-col gap-4 mt-6">
        <label className="flex flex-col gap-1">
          <span className="font-open-sans text-[14px] text-black">First Name</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Enter first name"
            className="border-[1px] border-[#E9E9E9] rounded-lg text-[16px] text-[#333333] font-open-sans p-3"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-open-sans text-[14px] text-black">Last Name</span>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Enter last name"
            className="border-[1px] border-[#E9E9E9] rounded-lg text-[16px] text-[#333333] font-open-sans p-3"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-open-sans text-[14px] text-black">Email Address</span>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) {
                setEmailError(null);
              }
            }}
            placeholder="Enter email address"
            aria-invalid={emailError ? true : undefined}
            aria-describedby={emailError ? "invite-email-error" : undefined}
            className={`border-[1px] rounded-lg text-[16px] text-[#333333] font-open-sans p-3 ${
              emailError ? "border-red-500" : "border-[#E9E9E9]"
            }`}
          />
          {emailError ? (
            <p
              id="invite-email-error"
              data-testid="invite-email-error"
              className="font-open-sans text-[13px] text-red-600 mt-1"
            >
              {emailError}
            </p>
          ) : null}
        </label>
      </div>

      <div className="flex items-center justify-end gap-3 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className={`border-[1px] border-[#E9E9E9] bg-white text-[#333333] font-raleway font-semibold text-[16px] px-6 py-2 rounded-lg${touchClass}`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleInvite}
          disabled={!isValid}
          className={`border-0 font-raleway font-semibold text-[16px] px-6 py-2 rounded-lg${touchClass} ${
            isValid
              ? isMobile
                ? "bg-primaryDark-0 text-white hover:bg-primaryDark-300"
                : "bg-primaryDark-0 text-white hover:bg-back-300"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          }`}
        >
          {INVITE_TEAM_MEMBER_SUBMIT_LABEL}
        </button>
      </div>
    </div>
  );
};

type InviteSentViewProps = {
  onAddAnother: () => void;
  onDone: () => void;
  isMobile?: boolean;
};

const InviteSentView: React.FC<InviteSentViewProps> = ({
  onAddAnother,
  onDone,
  isMobile = false,
}) => {
  // const touchClass = isMobile ? " touch-manipulation" : "";
  // const primaryHoverClass = isMobile ? "hover:bg-primaryDark-300" : "hover:bg-back-300";

  return (
    <div className="flex flex-col w-full mx-auto items-center text-center py-2">
      <div className="h-14 w-14 rounded-full bg-[#E9FBE9] flex items-center justify-center mb-4">
        <svg className="w-7 h-7 text-[#0F973D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <p className="font-raleway text-[18px] font-semibold text-black">{INVITE_SUCCESS_HEADING}</p>
      <p className="font-open-sans text-[14px] text-[#808080] mt-2 max-w-sm">
        Your team member will be invited and receive an email with instructions to join.
      </p>
      {/* <button
        type="button"
        onClick={onAddAnother}
        className={`bg-primaryDark-0 text-white font-raleway font-semibold text-[16px] border-0 w-full mt-8 py-3 rounded-lg ${primaryHoverClass}${touchClass}`}
      >
        Add another member
      </button>
      <button
        type="button"
        onClick={onDone}
        className={`border-[1px] border-[#E9E9E9] bg-white text-primaryDark-0 font-raleway font-semibold text-[16px] w-full mt-3 py-3 rounded-lg${touchClass}`}
      >
        Done
      </button> */}
    </div>
  );
};

export const InviteTeamMemberModal: React.FC<InviteTeamMemberModalProps> = ({
  isOpen,
  onClose,
  onInvite,
  isMobile = false,
  existingMemberEmails = [],
}) => {
  const [view, setView] = useState<"form" | "success">("form");
  const [formSessionKey, setFormSessionKey] = useState(0);
  const autoCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startFreshFormSession = () => {
    setFormSessionKey((key) => key + 1);
  };

  const clearAutoCloseTimeout = () => {
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearAutoCloseTimeout();
  }, []);

  useEffect(() => {
    if (isOpen) {
      setView("form");
      startFreshFormSession();
    } else {
      clearAutoCloseTimeout();
    }
  }, [isOpen]);

  const handleClose = () => {
    clearAutoCloseTimeout();
    setView("form");
    onClose();
  };

  const handleInvite = (member: TeamMemberInviteDetails) => {
    onInvite(member);
    setView("success");
    clearAutoCloseTimeout();
    autoCloseTimeoutRef.current = setTimeout(() => {
      autoCloseTimeoutRef.current = null;
      onClose();
    }, INVITE_SUCCESS_AUTO_CLOSE_MS);
  };

  const handleAddAnother = () => {
    clearAutoCloseTimeout();
    setView("form");
    startFreshFormSession();
  };

  return (
    <div
      className={`fixed inset-0 z-[100] ${isOpen ? "flex" : "hidden"} items-start justify-center p-4 sm:p-6`}
      data-testid="invite-team-member-modal"
    >
      <div className="fixed inset-0 bg-white bg-opacity-90 z-[99]" onClick={handleClose} />
      <dialog
        className={`z-[100] w-10/12 sm:w-5/12 max-w-lg h-auto bg-[#fff] relative rounded-2xl shadow-[#00000029] shadow-[0_0px_8px_rgba(0,0,0,0.16)] overflow-hidden flex flex-col${
          isMobile ? " top-8" : " top-16"
        }`}
        open={isOpen}
        onClose={handleClose}
        style={{ margin: 0 }}
      >
        <div className="flex flex-row justify-end p-4 pb-0">
          <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
            <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
          </span>
        </div>
        <div className="flex flex-col w-full m-auto px-4 pb-6">
          {view === "form" ? (
            <InviteTeamMemberForm
              key={formSessionKey}
              onCancel={handleClose}
              onInvite={handleInvite}
              isMobile={isMobile}
              existingMemberEmails={existingMemberEmails}
            />
          ) : (
            <InviteSentView
              onAddAnother={handleAddAnother}
              onDone={handleClose}
              isMobile={isMobile}
            />
          )}
        </div>
      </dialog>
    </div>
  );
};
